// Lib
import { defer, isEmpty, trim } from 'lodash';

// Utils
import { isCard, isClone } from '../../../common/elements/utils/elementTypeUtils';
import {
    getDisplayMode,
    getLinkedElementId,
    getTextContent,
} from '../../../common/elements/utils/elementPropertyUtils';
import { getEditorJsonContentLines } from '../../../common/tiptap/utils/jsonContentUtils/getEditorJsonContentLines';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';

// Selectors
import { getElements } from '../selectors/elementSelector';

// Actions
import { setElementTypeAndUpdateElement } from '../actions/elementActions';
import { startEditingElement } from '../selection/selectionActions';

// Constants
import {
    DOCUMENT_DEFAULT_TITLE,
    DOCUMENT_TITLE_EDITOR_KEY,
    DOCUMENT_TITLE_CHAR_LIMIT,
} from '../../../common/documents/documentConstants';
import { ELEMENT_DISPLAY_MODES } from '../../../common/elements/elementDisplayModeConstants';
import { UNICODE } from '../../../common/utils/stringConstants';
import { ElementType } from '../../../common/elements/elementTypes';

export const convertElementsToDocument = (elementIds: string[]) => (dispatch: Function, getState: Function) => {
    const state = getState();

    if (isEmpty(elementIds)) return null;

    const transactionId = getNewTransactionId();

    const elements = getElements(state);

    elementIds.forEach((elementId) => {
        const element = elements.get(elementId);

        // A clone has no content and may have a stale elementType, resolve both against the live
        // original
        const contentElement = isClone(element) ? elements.get(getLinkedElementId(element)) : element;

        if (!isCard(contentElement)) return null;

        const textContent = getTextContent(contentElement);
        const blockText = getEditorJsonContentLines(textContent);
        const firstLine = trim(blockText[0]) || '';

        // if the first line is longer than 60 characters,
        // use the first 59 characters and add the ellipsis character for a total of 60 chars
        const documentTitle =
            firstLine.length > DOCUMENT_TITLE_CHAR_LIMIT
                ? firstLine.substring(0, DOCUMENT_TITLE_CHAR_LIMIT - 1).concat(UNICODE.ELLIPSIS)
                : firstLine;

        dispatch(
            setElementTypeAndUpdateElement({
                id: elementId,
                elementType: ElementType.DOCUMENT_TYPE,
                changes: {
                    title: documentTitle || DOCUMENT_DEFAULT_TITLE,
                    displayMode: getDisplayMode(element) || ELEMENT_DISPLAY_MODES.ICON_VIEW,
                },
                transactionId,
                undoChanges: undefined,
            }),
        );
    });

    // Start editing document title upon conversion
    defer(() =>
        dispatch(
            startEditingElement({
                id: elementIds[0],
                editorId: undefined,
                editorKey: DOCUMENT_TITLE_EDITOR_KEY,
                // startEditingElement (selectionActions.js) has no type annotation, so its untyped
                // `transactionId = undefined` default gets inferred as literally `undefined` — this is
                // the only caller in the codebase passing a real transaction id.
                // oxlint-disable-next-line milanote-custom/no-chained-type-assertion -- bridging an untyped JS default; number and undefined don't overlap for a single cast
                transactionId: transactionId as unknown as undefined,
            }),
        ),
    );
};
